import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI } from '../services/api';
import './WorkloadDetailDrawer.css';

/**
 * Full configuration for one pod or deployment (SARVKUBE-63).
 *
 * The Workloads tables previously showed no configuration at all — clicking a row did
 * nothing, and seeing how a workload was set up meant dropping to kubectl. This reads the
 * LIVE object from the cluster, so it shows what is actually running rather than what
 * SarvKube was asked to deploy (those diverge whenever something is changed outside it).
 *
 * A drawer rather than a page on purpose: inspecting several pods in a row is the normal
 * debugging flow, and this keeps the table's page, filter and scroll position intact.
 */

// Kinds the API accepts an apply for — mirrors _EDITABLE in _paged.py. Offering Edit for
// anything else would produce a button that 405s.
const EDITABLE = new Set(['services', 'ingresses', 'deployments', 'statefulsets', 'configmaps']);

const TABS = [
  { id: 'config', label: 'Configuration' },
  { id: 'events', label: 'Events' },
  { id: 'related', label: 'Related' },
  { id: 'yaml', label: 'YAML' },
];

function Row({ label, children, mono }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="wd-row">
      <div className="wd-row-label">{label}</div>
      <div className={`wd-row-value${mono ? ' mono' : ''}`}>{children}</div>
    </div>
  );
}

function Pill({ tone, children }) {
  return <span className={`wd-pill wd-pill-${tone || 'neutral'}`}>{children}</span>;
}

/** requests/limits side by side — the ticket's first two bullets. */
function Resources({ r }) {
  const unset = !r.cpu_request && !r.cpu_limit && !r.memory_request && !r.memory_limit;
  return (
    <table className="wd-mini">
      <thead><tr><th /><th>Request</th><th>Limit</th></tr></thead>
      <tbody>
        <tr><td>CPU</td><td>{r.cpu_request || '—'}</td><td>{r.cpu_limit || '—'}</td></tr>
        <tr><td>Memory</td><td>{r.memory_request || '—'}</td><td>{r.memory_limit || '—'}</td></tr>
        {Object.entries(r.other_requests || {}).map(([k, v]) => (
          <tr key={k}><td>{k}</td><td>{v}</td><td>{(r.other_limits || {})[k] || '—'}</td></tr>
        ))}
        {Object.entries(r.other_limits || {})
          .filter(([k]) => !(k in (r.other_requests || {})))
          .map(([k, v]) => <tr key={k}><td>{k}</td><td>—</td><td>{v}</td></tr>)}
      </tbody>
      {unset && (
        <tfoot>
          <tr>
            <td colSpan={3} className="wd-note">
              No requests or limits set — this container can be scheduled anywhere and is
              first to be evicted under pressure.
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

function EnvTable({ env }) {
  if (!env.length) return <div className="wd-empty-inline">No environment variables.</div>;
  return (
    <table className="wd-mini wd-mini-wide">
      <thead><tr><th>Name</th><th>Value</th></tr></thead>
      <tbody>
        {env.map((e, i) => (
          <tr key={`${e.name}-${i}`}>
            <td className="mono">{e.name}</td>
            <td>
              {e.value !== null && e.value !== undefined ? (
                <span className="mono wd-env-value">{e.value}</span>
              ) : (
                /* Secret values are never sent to the browser — only where they come from.
                   Resolving them would make 'workloads: view' a way to read Secrets. */
                <span className="wd-env-ref">
                  <Pill tone={e.source === 'secret' ? 'warn' : 'neutral'}>{e.source || 'ref'}</Pill>
                  <span className="mono">{e.source_detail}</span>
                  {e.source === 'secret' && <em className="wd-hidden-note">value hidden</em>}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Container({ c, isInit }) {
  const [open, setOpen] = useState(!isInit);
  return (
    <div className="wd-container">
      <button className="wd-container-head" onClick={() => setOpen(o => !o)}>
        <span className={`wd-chev ${open ? 'open' : ''}`}>›</span>
        <span className="wd-container-name mono">{c.name}</span>
        {isInit && <Pill tone="neutral">init</Pill>}
        {c.state && (
          <Pill tone={c.state === 'running' ? 'ok' : c.state === 'terminated' ? 'bad' : 'warn'}>
            {c.state_reason || c.state}
          </Pill>
        )}
        {c.restart_count > 0 && <Pill tone="warn">{c.restart_count} restarts</Pill>}
      </button>
      {open && (
        <div className="wd-container-body">
          <Row label="Image" mono>{c.image}</Row>
          <Row label="Pull policy">{c.image_pull_policy}</Row>
          {c.state_message && <Row label="Message">{c.state_message}</Row>}
          {c.command?.length > 0 && <Row label="Command" mono>{c.command.join(' ')}</Row>}
          {c.args?.length > 0 && <Row label="Args" mono>{c.args.join(' ')}</Row>}

          <div className="wd-sub">Resources</div>
          <Resources r={c.resources} />

          <div className="wd-sub">Ports</div>
          {c.ports.length ? (
            <table className="wd-mini">
              <thead><tr><th>Container</th><th>Protocol</th><th>Name</th><th>Host</th></tr></thead>
              <tbody>
                {c.ports.map((p, i) => (
                  <tr key={i}>
                    <td className="mono">{p.container_port}</td>
                    <td>{p.protocol}</td>
                    <td>{p.name || '—'}</td>
                    <td>{p.host_port || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="wd-empty-inline">No ports exposed.</div>}

          <div className="wd-sub">Environment</div>
          <EnvTable env={c.env} />

          <div className="wd-sub">Volume mounts</div>
          {c.volume_mounts.length ? (
            <table className="wd-mini wd-mini-wide">
              <thead><tr><th>Path</th><th>Type</th><th>Source</th><th /></tr></thead>
              <tbody>
                {c.volume_mounts.map((m, i) => (
                  <tr key={i}>
                    <td className="mono">{m.mount_path}{m.sub_path ? `/${m.sub_path}` : ''}</td>
                    <td><Pill tone={m.kind === 'pvc' ? 'ok' : 'neutral'}>{m.kind}</Pill></td>
                    <td className="mono">{m.source || '—'}</td>
                    <td>{m.read_only && <span className="wd-note">read-only</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="wd-empty-inline">No volumes mounted.</div>}

          {Object.values(c.probes || {}).some(Boolean) && (
            <Row label="Probes">
              {Object.entries(c.probes).filter(([, v]) => v)
                .map(([k]) => k.replace('Probe', '')).join(', ')}
            </Row>
          )}
        </div>
      )}
    </div>
  );
}

/** Kind-specific rows. Each kind fails in its own way, so the fields that explain a failure
 *  differ — a DaemonSet gap means a nodeSelector or taint, a Job needs its backoff limit. */
function KindRows({ d }) {
  if (d.kind === 'Service') {
    const noSelector = !Object.keys(d.selector || {}).length;
    return <>
      <Row label="Type">{d.type}</Row>
      <Row label="Cluster IP" mono>{d.cluster_ip}</Row>
      <Row label="External" mono>{(d.external_ips || []).filter(Boolean).join(', ') || null}</Row>
      <Row label="External name" mono>{d.external_name}</Row>
      <Row label="Selector" mono>
        {noSelector
          ? <span className="wd-note">none — this Service has no endpoints unless they are
              managed manually</span>
          : Object.entries(d.selector).map(([k, v]) => `${k}=${v}`).join(', ')}
      </Row>
      {d.ports?.length > 0 && (
        <>
          <div className="wd-section">Ports</div>
          <table className="wd-mini wd-mini-wide">
            <thead><tr><th>Name</th><th>Port</th><th>Target</th><th>Node port</th><th>Protocol</th></tr></thead>
            <tbody>
              {d.ports.map((p, i) => (
                <tr key={i}>
                  <td>{p.name || '—'}</td>
                  <td className="mono">{p.port}</td>
                  <td className="mono">{String(p.target_port ?? '—')}</td>
                  <td className="mono">{p.node_port || '—'}</td>
                  <td>{p.protocol}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>;
  }
  if (d.kind === 'Ingress') {
    return <>
      <Row label="Class">
        {d.class || <span className="wd-note">none set — most controllers ignore an ingress
          with no class</span>}
      </Row>
      <Row label="Load balancer" mono>{(d.load_balancer || []).join(', ') || null}</Row>
      {d.rules?.length > 0 && (
        <>
          <div className="wd-section">Routes</div>
          <table className="wd-mini wd-mini-wide">
            <thead><tr><th>Host</th><th>Path</th><th>Type</th><th>Backend</th></tr></thead>
            <tbody>
              {d.rules.map((r, i) => (
                <tr key={i}>
                  <td className="mono">{r.host}</td>
                  <td className="mono">{r.path}</td>
                  <td>{r.path_type || '—'}</td>
                  {/* The backend is what to check when an ingress returns 503: it may name a
                      service that does not exist or has no endpoints. */}
                  <td className="mono">{r.service}:{r.port}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {d.tls?.length > 0 && (
        <>
          <div className="wd-section">TLS</div>
          {d.tls.map((t, i) => (
            <Row key={i} label={t.secret || 'secret'} mono>{(t.hosts || []).join(', ')}</Row>
          ))}
        </>
      )}
    </>;
  }
  if (d.kind === 'StatefulSet') {
    return <>
      <Row label="Governing service" mono>{d.service_name}</Row>
      <Row label="Pod management">{d.pod_management_policy}</Row>
      <Row label="Update strategy">
        {d.strategy}
        {d.strategy_detail?.partition !== undefined && (
          <span className="wd-note"> (partition {d.strategy_detail.partition})</span>
        )}
      </Row>
      <Row label="Selector" mono>
        {Object.entries(d.selector || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
      </Row>
    </>;
  }
  if (d.kind === 'DaemonSet') {
    const gap = (d.desired || 0) - (d.ready || 0);
    return <>
      <Row label="Scheduled">
        {d.ready}/{d.desired} ready · {d.available} available
        {gap > 0 && (
          <span className="wd-note">
            {' '}— {gap} node{gap === 1 ? '' : 's'} without a ready pod, usually a
            nodeSelector or taint rather than a failing pod
          </span>
        )}
      </Row>
      {d.misscheduled > 0 && (
        <Row label="Misscheduled">
          <Pill tone="warn">{d.misscheduled}</Pill>
          <span className="wd-note"> running on nodes that no longer match</span>
        </Row>
      )}
      <Row label="Update strategy">{d.strategy}</Row>
    </>;
  }
  if (d.kind === 'Job') {
    return <>
      <Row label="Completions">
        {d.succeeded}/{d.completions ?? '—'} succeeded
        {d.failed > 0 && <> · <Pill tone="bad">{d.failed} failed</Pill></>}
        {d.active > 0 && <> · <Pill tone="warn">{d.active} active</Pill></>}
      </Row>
      <Row label="Parallelism">{d.parallelism}</Row>
      <Row label="Backoff limit">{d.backoff_limit}</Row>
      <Row label="Active deadline">{d.active_deadline ? `${d.active_deadline}s` : null}</Row>
      <Row label="Started">{d.start_time && new Date(d.start_time).toLocaleString()}</Row>
      <Row label="Completed">{d.completion_time && new Date(d.completion_time).toLocaleString()}</Row>
    </>;
  }
  if (d.kind === 'CronJob') {
    return <>
      <Row label="Schedule" mono>{d.schedule}</Row>
      <Row label="Suspended">
        {d.suspend ? <Pill tone="warn">yes — not running</Pill> : <Pill tone="ok">no</Pill>}
      </Row>
      <Row label="Concurrency">{d.concurrency_policy}</Row>
      <Row label="Last schedule">{d.last_schedule && new Date(d.last_schedule).toLocaleString()}</Row>
      <Row label="Last successful">{d.last_successful && new Date(d.last_successful).toLocaleString()}</Row>
      {d.active_jobs?.length > 0 && <Row label="Running jobs" mono>{d.active_jobs.join(', ')}</Row>}
      <Row label="History kept">
        {d.successful_history ?? '—'} successful, {d.failed_history ?? '—'} failed
      </Row>
    </>;
  }
  return null;
}

export default function WorkloadDetailDrawer({
  clusterId, kind, namespace, name, onClose,
  // Path segment for the API. Defaults from `kind` so the existing Pods/Deployments callers
  // keep working unchanged.
  resource,
  // Lets a caller open straight on the YAML tab instead of Configuration — used by
  // Deployments.js's "Edit" menu item, so picking Edit lands directly on the editable
  // manifest instead of requiring a second click from Configuration.
  initialTab,
  // Same caller: skip the extra "Edit" button click too, landing in edit MODE (not just
  // the YAML view) the moment the manifest finishes loading.
  autoEdit,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab || 'config');
  const [copied, setCopied] = useState(false);
  // YAML editing. Kept in the drawer because the YAML tab already has the live manifest —
  // a separate edit screen would just fetch the same thing again.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);
  // Revision history + rollback, surfaced IN the YAML tab rather than a separate modal —
  // reported live: "why is history required? if want to run previous version can't it be
  // show from yaml edit pages?" The backend's own history endpoint only returns
  // revision/image/created_at/replicas per revision, not that revision's full manifest, so
  // this can't be "load old YAML into the textarea" — it calls the existing rollback
  // endpoint directly (same one EditDeploymentModal used), just reachable from here instead
  // of a separate component. Deployment-only: rollout history is a Deployment/ReplicaSet
  // concept, not something StatefulSets/Pods/etc. have.
  const [history, setHistory] = useState([]);
  const [rollingBack, setRollingBack] = useState(null);

  // "Pod" -> "pods", "StatefulSet" -> "statefulsets". Explicit `resource` wins.
  const path = resource || `${kind.toLowerCase()}s`;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await clustersAPI.getWorkloadDetail(path, clusterId, namespace, name);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || `Could not load ${kind} details.`);
    } finally { setLoading(false); }
  }, [clusterId, path, kind, namespace, name]);

  useEffect(() => { load(); }, [load]);

  // autoEdit: enter edit mode as soon as the manifest is actually available — can't just
  // start `editing` true at mount, since `draft` needs the real fetched YAML to seed from,
  // not an empty string a user would then "save" as a blank manifest.
  useEffect(() => {
    if (autoEdit && data?.yaml && !editing) { setDraft(data.yaml); setEditing(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit, data]);

  useEffect(() => {
    if (kind !== 'Deployment' || tab !== 'yaml') return;
    clustersAPI.deploymentHistory(clusterId, namespace, name)
      .then(r => setHistory(r.data?.history || []))
      .catch(() => setHistory([]));
  }, [kind, tab, clusterId, namespace, name]);

  const handleRollback = async (revision) => {
    setRollingBack(revision);
    try {
      await clustersAPI.rollbackDeployment(clusterId, namespace, name, revision);
      setApplyMsg({ ok: true, text: `Rolled back to revision ${revision}.` });
      load();
    } catch (e) {
      setApplyMsg({ ok: false, text: e.response?.data?.detail || 'Rollback failed' });
    } finally {
      setRollingBack(null);
    }
  };

  // Escape closes, matching the logs drawer.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const applyYaml = async () => {
    setApplying(true); setApplyMsg(null);
    try {
      const r = await clustersAPI.applyWorkloadYaml(path, clusterId, namespace, name, draft);
      setApplyMsg({ ok: true, text: r.data?.output || 'Applied.' });
      setEditing(false);
      load();                      // re-read, so the panel shows what the cluster now has
    } catch (e) {
      // kubectl's own message names the offending field; anything invented would not.
      setApplyMsg({ ok: false, text: e.response?.data?.detail || 'Apply failed.' });
    } finally { setApplying(false); }
  };

  const copyYaml = () => {
    navigator.clipboard?.writeText(data?.yaml || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const phaseTone = (p) =>
    ['Running', 'Succeeded', 'Active'].includes(p) ? 'ok'
      : ['Failed', 'CrashLoopBackOff', 'Error'].includes(p) ? 'bad' : 'warn';

  return (
    <div className="wd-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="wd-drawer">
        <div className="wd-header">
          <div>
            <div className="wd-title mono">{name}</div>
            <div className="wd-sub-title">
              {kind} · {namespace}
              {data?.phase && <> · <Pill tone={phaseTone(data.phase)}>{data.phase}</Pill></>}
              {/* Readiness at a glance, in each kind's own units: replicas for
                  Deployment/StatefulSet, nodes for DaemonSet, completions for a Job. */}
              {['Deployment', 'StatefulSet'].includes(data?.kind) && data.replicas !== undefined && (
                <> · <Pill tone={data.ready_replicas === data.replicas ? 'ok' : 'warn'}>
                  {data.ready_replicas}/{data.replicas} ready
                </Pill></>
              )}
              {data?.kind === 'DaemonSet' && (
                <> · <Pill tone={data.ready === data.desired ? 'ok' : 'warn'}>
                  {data.ready}/{data.desired} nodes
                </Pill></>
              )}
              {data?.kind === 'Job' && (
                <> · <Pill tone={data.failed > 0 ? 'bad' : data.active > 0 ? 'warn' : 'ok'}>
                  {data.failed > 0 ? `${data.failed} failed`
                    : data.active > 0 ? `${data.active} active`
                    : `${data.succeeded} succeeded`}
                </Pill></>
              )}
              {data?.kind === 'CronJob' && (
                <> · <Pill tone={data.suspend ? 'warn' : 'ok'}>
                  {data.suspend ? 'suspended' : data.schedule}
                </Pill></>
              )}
            </div>
          </div>
          <div className="wd-header-actions">
            <button className="wd-btn" onClick={load} disabled={loading}>
              {loading ? '…' : '↺ Refresh'}
            </button>
            <button className="wd-btn wd-btn-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="wd-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`wd-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.id === 'events' && data?.events?.length > 0 && (
                <span className="wd-tab-count">{data.events.length}</span>
              )}
              {t.id === 'related' && data?.related?.pods?.length > 0 && (
                <span className="wd-tab-count">{data.related.pods.length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="wd-body">
          {loading && <div className="wd-empty">Loading…</div>}
          {!loading && error && <div className="wd-error">{error}</div>}

          {!loading && !error && data && tab === 'config' && (
            <>
              <div className="wd-section">Overview</div>
              <Row label="Namespace" mono>{data.namespace}</Row>
              <Row label="Created">{data.created_at && new Date(data.created_at).toLocaleString()}</Row>
              {data.kind === 'Pod' ? (
                <>
                  <Row label="Node" mono>{data.node}</Row>
                  <Row label="Pod IP" mono>{data.pod_ip}</Row>
                  <Row label="QoS class">{data.qos_class}</Row>
                </>
              ) : (
                <>
                  <Row label="Strategy">
                    {data.strategy}
                    {data.strategy_detail?.maxSurge && (
                      <span className="wd-note">
                        {' '}(surge {data.strategy_detail.maxSurge}, unavailable {data.strategy_detail.maxUnavailable})
                      </span>
                    )}
                  </Row>
                  <Row label="Selector" mono>
                    {Object.entries(data.selector || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
                  </Row>
                </>
              )}
              <KindRows d={data} />
              <Row label="Service account" mono>{data.service_account}</Row>
              {data.host_network && <Row label="Host network"><Pill tone="warn">enabled</Pill></Row>}
              {Object.keys(data.node_selector || {}).length > 0 && (
                <Row label="Node selector" mono>
                  {Object.entries(data.node_selector).map(([k, v]) => `${k}=${v}`).join(', ')}
                </Row>
              )}
              {data.owner && (
                <Row label="Owned by">{data.owner.kind} <span className="mono">{data.owner.name}</span></Row>
              )}

              {/* A StatefulSet's storage lives in volumeClaimTemplates, not in a PVC list:
                  each replica gets its own PVC named <template>-<set>-<ordinal>. This is the
                  StatefulSet-specific field, and where they usually break. */}
              {data.volume_claim_templates?.length > 0 && (
                <>
                  <div className="wd-section">Volume claim templates</div>
                  <table className="wd-mini wd-mini-wide">
                    <thead><tr><th>Name</th><th>Size</th><th>Access modes</th><th>Storage class</th></tr></thead>
                    <tbody>
                      {data.volume_claim_templates.map(t => (
                        <tr key={t.name}>
                          <td className="mono">{t.name}</td>
                          <td>{t.storage || '—'}</td>
                          <td>{(t.access_modes || []).join(', ') || '—'}</td>
                          <td className="mono">{t.storage_class || '(default)'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="wd-note">
                    Each replica gets its own claim, named
                    {' '}<span className="mono">
                      {data.volume_claim_templates[0].name}-{data.name}-0
                    </span>, -1, and so on.
                  </div>
                </>
              )}

              {/* PVCs called out on their own — the ticket asks for them by name, and
                  hunting through per-container mounts to find them is the reported friction. */}
              {data.pvcs?.length > 0 && (
                <>
                  <div className="wd-section">Persistent Volume Claims</div>
                  {data.pvcs.map(p => <Row key={p} label="PVC" mono>{p}</Row>)}
                </>
              )}

              {data.conditions?.length > 0 && (
                <>
                  <div className="wd-section">Conditions</div>
                  <table className="wd-mini wd-mini-wide">
                    <thead><tr><th>Type</th><th>Status</th><th>Reason</th></tr></thead>
                    <tbody>
                      {data.conditions.map((c, i) => (
                        <tr key={i}>
                          <td>{c.type}</td>
                          <td><Pill tone={c.status === 'True' ? 'ok' : 'bad'}>{c.status}</Pill></td>
                          <td>{c.reason || '—'}{c.message ? ` — ${c.message}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className="wd-section">
                Containers
                {data.init_containers?.length > 0 && (
                  <span className="wd-note"> · {data.init_containers.length} init</span>
                )}
              </div>
              {data.init_containers?.map(c => <Container key={c.name} c={c} isInit />)}
              {data.containers?.map(c => <Container key={c.name} c={c} />)}
            </>
          )}

          {!loading && !error && data && tab === 'events' && (
            data.events?.length ? (
              <table className="wd-mini wd-mini-wide">
                <thead><tr><th>Type</th><th>Reason</th><th>Message</th><th>Count</th><th>Last seen</th></tr></thead>
                <tbody>
                  {data.events.map((e, i) => (
                    <tr key={i}>
                      <td><Pill tone={e.type === 'Warning' ? 'warn' : 'neutral'}>{e.type}</Pill></td>
                      <td>{e.reason}</td>
                      <td className="wd-msg">{e.message}</td>
                      <td>{e.count || 1}</td>
                      <td>{e.last_seen ? new Date(e.last_seen).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="wd-empty">
                No recent events. Kubernetes only keeps events for about an hour, so a
                healthy, long-running object usually has none.
              </div>
            )
          )}

          {!loading && !error && data && tab === 'related' && (
            <>
              {data.related?.owner && (
                <>
                  <div className="wd-section">Owner</div>
                  <Row label={data.related.owner.kind} mono>{data.related.owner.name}</Row>
                </>
              )}
              <div className="wd-section">Pods</div>
              {data.related?.pods?.length ? (
                <table className="wd-mini wd-mini-wide">
                  <thead><tr><th>Name</th><th>Phase</th><th>Node</th></tr></thead>
                  <tbody>
                    {data.related.pods.map(p => (
                      <tr key={p.name}>
                        <td className="mono">{p.name}</td>
                        <td><Pill tone={phaseTone(p.phase)}>{p.phase}</Pill></td>
                        <td className="mono">{p.node || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="wd-empty">
                  {data.kind === 'Pod'
                    ? 'Related pods are listed for Deployments.'
                    : 'No pods match this deployment’s selector.'}
                </div>
              )}
            </>
          )}

          {!loading && !error && data && tab === 'yaml' && (
            <div className="wd-yaml-wrap">
              {kind === 'Deployment' && history.length > 1 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                              marginBottom: 10, padding: '8px 10px', borderRadius: 6,
                              background: 'var(--surface-2)', fontSize: 12 }}>
                  <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Previous versions:</span>
                  {history.slice(0, 5).map((h, i) => (
                    <button key={h.revision} className="wd-btn" disabled={i === 0 || rollingBack !== null}
                      title={h.image}
                      onClick={() => handleRollback(h.revision)}>
                      {i === 0 ? `#${h.revision} (current)` :
                        rollingBack === h.revision ? 'Rolling back…' : `↩ #${h.revision}`}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end',
                            marginBottom: 8, alignItems: 'center' }}>
                {applyMsg && (
                  <span style={{ flex: 1, fontSize: 12,
                    color: applyMsg.ok ? 'var(--color-success-500)' : 'var(--color-danger-500)',
                    fontFamily: applyMsg.ok ? 'inherit' : 'monospace', wordBreak: 'break-word' }}>
                    {applyMsg.text}
                  </span>
                )}
                <button className="wd-btn" onClick={copyYaml}>
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
                {EDITABLE.has(path) && !editing && (
                  <button className="wd-btn" onClick={() => {
                    setDraft(data.yaml || ''); setEditing(true); setApplyMsg(null);
                  }}>Edit</button>
                )}
                {editing && (
                  <>
                    <button className="wd-btn" onClick={() => {
                      setEditing(false); setApplyMsg(null);
                    }}>Cancel</button>
                    <button className="wd-btn" style={{ borderColor: 'var(--brand)',
                      color: '#fff', background: 'var(--brand)' }}
                      disabled={applying} onClick={applyYaml}>
                      {applying ? 'Applying…' : 'Apply'}
                    </button>
                  </>
                )}
              </div>
              {editing && (
                /* kubectl apply MERGES, so deleting a key here leaves it on the object. Worth
                   saying, because "I removed it and it is still there" otherwise reads as a
                   bug in SarvKube. */
                <div className="wd-note" style={{ marginBottom: 8 }}>
                  Changes are applied with <code>kubectl apply</code>, which merges — editing or
                  adding a field works, but deleting one here will not remove it from the object.
                </div>
              )}
              {editing ? (
                /* A plain textarea, not a syntax-highlighting editor: this is for changing a
                   host or a port, and the server validates the result anyway. */
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  spellCheck={false}
                  className="wd-yaml"
                  style={{ width: '100%', minHeight: 360, resize: 'vertical', border: 'none',
                           outline: 'none' }}
                />
              ) : (
                <pre className="wd-yaml">{data.yaml || 'YAML unavailable.'}</pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
